/**
 * <FinanceEntryFields /> — single source of truth for the Department →
 * Category → Sub-category → Cost-Type (read-only) → AOP → Vendor → Pay
 * Mode → Source → Drive → Month → Amount → Date → Description set used
 * across Expenses, Reimbursements, Purchase Requests and the Asset
 * Register.
 *
 * Parent owns the `value` object and an `onChange(patch)` callback that
 * receives only the fields that changed.
 *
 * Required field whitelist (used by `isFinanceEntryValid`):
 *   project_id, department, category, sub_category, aop, vendor_id|vendor,
 *   month, amount, date — plus payment_mode/source_id, but ONLY for
 *   Founder/Admin (see Feb 2026 Form Cleanup below).
 *
 * Feb 2026 — Form Cleanup: "Pay Mode" and "Source (Bank/Card)" are
 * Finance-internal bookkeeping fields a regular employee filing a PR/
 * reimbursement has no reason to know (which card the company will
 * eventually pay from). They're now visible/required ONLY for
 * Founder/Admin; every other role never sees them and is never blocked by
 * them. Also — the inline "+ Add new vendor" option was removed here (use
 * the Vendor Registry, or the Expenses Ledger's own quick-add, instead).
 *
 * Feb 2026 — Intelligent Cascading: picking a Project auto-fills
 * Department with that project's Business Unit whenever it matches one of
 * the real Finance department names (BU1/BU2/BU3/etc.) — silently skipped
 * otherwise (e.g. "Operations" isn't a Finance department).
 *
 * Drive link is optional; everything else must be filled before the
 * Submit button is enabled.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { sortSources } from "@/lib/finance";
import { useAuth } from "@/context/AuthContext";

const PAY_MODES = ["Bank Transfer", "UPI", "Card", "Cash", "Cheque"];
const RESTRICTED_FIELD_ROLES = ["founder", "admin"];
export const canSeeFinanceRestrictedFields = (role) => RESTRICTED_FIELD_ROLES.includes(role);

export function isFinanceEntryValid(v, { categoryKey = "category", subCategoryKey = "sub_category", role } = {}) {
  if (!v) return false;
  // Backward-compatible default: if a caller doesn't pass `role` at all,
  // stay strict (old behaviour) rather than silently relaxing validation.
  const restricted = role ? canSeeFinanceRestrictedFields(role) : true;
  const checks = [v.project_id, v.department, v[categoryKey], v[subCategoryKey], v.aop];
  if (restricted) checks.push(v.payment_mode, v.source_id);
  checks.push(v.month, v.date);
  if (checks.some((x) => !x && x !== 0)) return false;
  if (!v.amount && v.amount !== 0) return false;
  // vendor: either vendor_id or vendor display name required
  if (!v.vendor_id && !(v.vendor && String(v.vendor).trim())) return false;
  if (Number(v.amount) <= 0) return false;
  return true;
}

export default function FinanceEntryFields({ value, onChange, testidPrefix = "fe", categoryKey = "category", subCategoryKey = "sub_category" }) {
  const { user } = useAuth();
  const canSeeRestricted = canSeeFinanceRestrictedFields(user?.role);
  const [tree, setTree] = useState([]);            // [{department, categories: [{category, sub_categories:[{sub_category,cost_type}]}]}]
  const [aopOptions, setAopOptions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sources, setSources] = useState([]);
  // Phase 138 — per-dropdown error state. A denied fetch now surfaces a
  // clear "Access denied" note in that specific field instead of silently
  // rendering an empty list that looks like "there's just nothing to pick".
  const [errors, setErrors] = useState({});

  const reloadVendors = () =>
    api.get("/admin/vendors").then(({ data }) => { setVendors(data || []); setErrors((e) => ({ ...e, vendors: null })); })
      .catch((err) => setErrors((e) => ({ ...e, vendors: err?.response?.status === 403 ? "denied" : "error" })));

  // Allow callers to store the canonical Category/Sub-category under custom
  // keys (Assets uses `finance_category` / `finance_sub_category` to avoid
  // clashing with the asset's hardware `category`).
  const cat = value[categoryKey];
  const sub = value[subCategoryKey];

  useEffect(() => {
    // Phase 138 — each dropdown fetch now runs independently (no more
    // Promise.all-with-one-uncaught-rejection wiping out every other field)
    // and records its own 403 vs generic-error state so the UI can tell
    // the difference between "you're not allowed" and "nothing exists yet".
    api.get("/finance/categories")
      .then(({ data }) => { setTree(data?.tree || []); setAopOptions(data?.aop_options || []); setErrors((e) => ({ ...e, categories: null })); })
      .catch((err) => setErrors((e) => ({ ...e, categories: err?.response?.status === 403 ? "denied" : "error" })));
    api.get("/projects")
      .then(({ data }) => { setProjects(data || []); setErrors((e) => ({ ...e, projects: null })); })
      .catch((err) => setErrors((e) => ({ ...e, projects: err?.response?.status === 403 ? "denied" : "error" })));
    reloadVendors();
    api.get("/finance/sources")
      .then(({ data }) => { setSources(data || []); setErrors((e) => ({ ...e, sources: null })); })
      .catch((err) => setErrors((e) => ({ ...e, sources: err?.response?.status === 403 ? "denied" : "error" })));
  }, []);

  const deptNode = useMemo(() => tree.find((d) => d.department === value.department), [tree, value.department]);
  const catNode = useMemo(() => deptNode?.categories.find((c) => c.category === cat), [deptNode, cat]);
  const subList = catNode?.sub_categories || [];
  const costType = useMemo(() => subList.find((s) => s.sub_category === sub)?.cost_type, [subList, sub]);
  const sortedSources = useMemo(() => sortSources(sources), [sources]);

  // Suggest Cost Type + default AOP whenever sub-category changes, but only
  // when the user hasn't already typed a custom value. Manual entry wins.
  useEffect(() => {
    if (!costType) return;
    const patch = {};
    if (!value.cost_type) patch.cost_type = costType;
    if (!value.aop) patch.aop = costType;
    if (Object.keys(patch).length > 0) onChange(patch);
  }, [costType]);

  const set = (k, v) => onChange({ [k]: v });

  // Inline "+ Add new vendor" (Name / email / category / note only). Creates a
  // real Vendor Registry row via the lightweight quick-vendor endpoint so it
  // shows up here and everywhere else immediately, then auto-selects it.
  const EMPTY_VENDOR = { name: "", email: "", category: "", notes: "" };
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [newVendor, setNewVendor] = useState(EMPTY_VENDOR);
  const [savingVendor, setSavingVendor] = useState(false);
  const submitNewVendor = async () => {
    if (!newVendor.name.trim() || !newVendor.email.trim()) { toast.error("Name and email are required"); return; }
    setSavingVendor(true);
    try {
      const { data } = await api.post("/procurement/quick-vendor", {
        name: newVendor.name.trim(),
        email: newVendor.email.trim(),
        category: newVendor.category.trim() || null,
        notes: newVendor.notes.trim() || null,
      });
      await reloadVendors();
      if (data?.id) set("vendor_id", data.id);
      toast.success(`Added "${data.company || data.name}" to the Vendor Registry`);
      setNewVendor(EMPTY_VENDOR);
      setAddVendorOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add vendor");
    } finally {
      setSavingVendor(false);
    }
  };

  // Feb 2026 — Intelligent Cascading: Project → Department (BU).
  const onProjectChange = (v) => {
    const p = projects.find((x) => x.id === v);
    const bu = p?.business_unit;
    if (bu && bu !== value.department && tree.some((d) => d.department === bu)) {
      onChange({ project_id: v, department: bu, [categoryKey]: "", [subCategoryKey]: "", cost_type: "" });
    } else {
      onChange({ project_id: v });
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-fields`}>
      {/* Row 1 — Project / Department */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="label-overline">Project *</Label>
          <Select value={value.project_id || ""} onValueChange={onProjectChange}>
            <SelectTrigger className="rounded-sm mt-1" data-testid={`${testidPrefix}-project`}><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          {errors.projects && <p className="text-[11px] text-red-600 mt-1" data-testid={`${testidPrefix}-project-error`}>{errors.projects === "denied" ? "Access denied — ask a Founder/Admin to grant you access." : "Couldn't load projects. Try refreshing."}</p>}
        </div>
        <div>
          <Label className="label-overline">Department *</Label>
          <Select value={value.department || ""} onValueChange={(v) => onChange({ department: v, [categoryKey]: "", [subCategoryKey]: "", cost_type: "" })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid={`${testidPrefix}-department`}><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>{tree.map((d) => <SelectItem key={d.department} value={d.department}>{d.department}</SelectItem>)}</SelectContent>
          </Select>
          {errors.categories && <p className="text-[11px] text-red-600 mt-1" data-testid={`${testidPrefix}-department-error`}>{errors.categories === "denied" ? "Access denied — ask a Founder/Admin to grant you Finance access." : "Couldn't load departments. Try refreshing."}</p>}
        </div>
      </div>

      {/* Row 2 — Category / Sub-category / Cost Type (read-only) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="label-overline">Category *</Label>
          <Select value={cat || ""} onValueChange={(v) => onChange({ [categoryKey]: v, [subCategoryKey]: "", cost_type: "" })} disabled={!value.department}>
            <SelectTrigger className="rounded-sm mt-1" data-testid={`${testidPrefix}-category`}><SelectValue placeholder={value.department ? "Pick category" : "Pick department first"} /></SelectTrigger>
            <SelectContent>{deptNode?.categories.map((c) => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">Sub-category *</Label>
          <Select value={sub || ""} onValueChange={(v) => onChange({ [subCategoryKey]: v })} disabled={!cat}>
            <SelectTrigger className="rounded-sm mt-1" data-testid={`${testidPrefix}-subcategory`}><SelectValue placeholder={cat ? "Pick sub-category" : "Pick category first"} /></SelectTrigger>
            <SelectContent>{subList.map((s) => <SelectItem key={s.sub_category} value={s.sub_category}>{s.sub_category}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">Cost Type</Label>
          <Input
            list={`${testidPrefix}-cost-type-options`}
            className="rounded-sm mt-1 font-mono text-xs"
            value={value.cost_type || ""}
            onChange={(e) => set("cost_type", e.target.value)}
            placeholder={costType ? `Suggested: ${costType}` : "Type or pick from list…"}
            data-testid={`${testidPrefix}-cost-type`}
          />
          <datalist id={`${testidPrefix}-cost-type-options`}>
            {costType && <option value={costType} />}
            {Array.from(new Set(aopOptions)).map((o) => <option key={o} value={o} />)}
          </datalist>
        </div>
      </div>

      {/* Row 3 — AOP / Vendor / Pay Mode (Pay Mode: Founder/Admin only) */}
      <div className={`grid grid-cols-1 gap-3 ${canSeeRestricted ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        <div>
          <Label className="label-overline">AOP *</Label>
          <Input
            list={`${testidPrefix}-aop-options`}
            className="rounded-sm mt-1"
            value={value.aop || ""}
            onChange={(e) => set("aop", e.target.value)}
            placeholder="Type or pick from list…"
            data-testid={`${testidPrefix}-aop`}
          />
          <datalist id={`${testidPrefix}-aop-options`}>
            {aopOptions.map((o) => <option key={o} value={o} />)}
          </datalist>
        </div>
        <div>
          <Label className="label-overline">Vendor *</Label>
          <Select value={value.vendor_id || ""} onValueChange={(v) => set("vendor_id", v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid={`${testidPrefix}-vendor`}><SelectValue placeholder="Select vendor" /></SelectTrigger>
            <SelectContent>
              {vendors.length === 0 && !errors.vendors && <div className="px-2 py-1 text-xs text-slate-400">No vendors yet</div>}
              {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.company || v.name}</SelectItem>)}
              <div className="border-t border-slate-100 mt-1 pt-1">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setAddVendorOpen(true)}
                  className="w-full text-left px-2 py-1.5 text-xs font-medium text-[#F54800] hover:bg-orange-50 rounded-sm flex items-center gap-1.5"
                  data-testid={`${testidPrefix}-add-vendor`}
                >
                  <Plus size={12} /> Add new vendor
                </button>
              </div>
            </SelectContent>
          </Select>
          {errors.vendors && <p className="text-[11px] text-red-600 mt-1" data-testid={`${testidPrefix}-vendor-error`}>{errors.vendors === "denied" ? "Access denied — ask a Founder/Admin to grant you access." : "Couldn't load vendors. Try refreshing."}</p>}
        </div>
        {canSeeRestricted && (
          <div>
            <Label className="label-overline">Pay Mode *</Label>
            <Select value={value.payment_mode || ""} onValueChange={(v) => set("payment_mode", v)}>
              <SelectTrigger className="rounded-sm mt-1" data-testid={`${testidPrefix}-paymode`}><SelectValue placeholder="Select pay mode" /></SelectTrigger>
              <SelectContent>{PAY_MODES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Row 4 — Source / Month / Drive (Source: Founder/Admin only) */}
      <div className={`grid grid-cols-1 gap-3 ${canSeeRestricted ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {canSeeRestricted && (
          <div>
            <Label className="label-overline">Source (bank / card) *</Label>
            <Select value={value.source_id || ""} onValueChange={(v) => set("source_id", v)}>
              <SelectTrigger className="rounded-sm mt-1" data-testid={`${testidPrefix}-source`}><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                {sortedSources.length === 0 && !errors.sources && <div className="px-2 py-1 text-xs text-slate-400">No sources yet — ask Admin to add one.</div>}
                {sortedSources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.type === "card" ? "Card" : "Bank"})</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.sources && <p className="text-[11px] text-red-600 mt-1" data-testid={`${testidPrefix}-source-error`}>{errors.sources === "denied" ? "Access denied — ask a Founder/Admin to grant you access." : "Couldn't load sources. Try refreshing."}</p>}
          </div>
        )}
        <div>
          <Label className="label-overline">Month *</Label>
          <Input type="month" className="rounded-sm mt-1" value={value.month || ""} onChange={(e) => set("month", e.target.value)} data-testid={`${testidPrefix}-month`} />
        </div>
        <div>
          <Label className="label-overline">Drive link</Label>
          <Input className="rounded-sm mt-1" placeholder="https://drive.google.com/…" value={value.drive_link || ""} onChange={(e) => set("drive_link", e.target.value)} data-testid={`${testidPrefix}-drive`} />
        </div>
      </div>

      <Dialog open={addVendorOpen} onOpenChange={(o) => { if (!savingVendor) { setAddVendorOpen(o); if (!o) setNewVendor(EMPTY_VENDOR); } }}>
        <DialogContent className="rounded-sm max-w-sm" data-testid={`${testidPrefix}-add-vendor-dialog`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus size={16} /> Add new vendor</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-1">
            <div>
              <Label className="label-overline">Name *</Label>
              <Input className="rounded-sm mt-1" value={newVendor.name} onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })} data-testid={`${testidPrefix}-add-vendor-name`} />
            </div>
            <div>
              <Label className="label-overline">Email *</Label>
              <Input type="email" className="rounded-sm mt-1" value={newVendor.email} onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })} data-testid={`${testidPrefix}-add-vendor-email`} />
            </div>
            <div>
              <Label className="label-overline">Category</Label>
              <Input className="rounded-sm mt-1" value={newVendor.category} onChange={(e) => setNewVendor({ ...newVendor, category: e.target.value })} placeholder="e.g. Prototyping, Injection Moulding" data-testid={`${testidPrefix}-add-vendor-category`} />
            </div>
            <div>
              <Label className="label-overline">Note</Label>
              <Input className="rounded-sm mt-1" value={newVendor.notes} onChange={(e) => setNewVendor({ ...newVendor, notes: e.target.value })} data-testid={`${testidPrefix}-add-vendor-note`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAddVendorOpen(false)} disabled={savingVendor}>Cancel</Button>
            <Button onClick={submitNewVendor} disabled={savingVendor} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid={`${testidPrefix}-add-vendor-save`}>{savingVendor ? "Adding…" : "Add vendor"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
